import { useEffect, useState } from "react";

import { downloadFile } from "@/core/utils";
import { downloadEmbarcacaoImagem } from "@/modules/embarcacoes/embarcacoes.gateway";
import type { EmbarcacaoImagemTipo } from "@/modules/embarcacoes/embarcacoes.types";

interface ImagemArquivo {
  readonly bytes: Uint8Array;
  readonly mimeType: string;
}

interface UseEmbarcacaoImagemArquivoParams {
  readonly id: string;
  readonly tipo: EmbarcacaoImagemTipo;
  /** Endereço da imagem: quando muda, o arquivo é lido de novo. */
  readonly url: string;
  /** Nome com que o arquivo é salvo. */
  readonly fileName: string;
}

/**
 * Arquivo de uma imagem da embarcação (HU 1751/2673). O arquivo é lido em memória pelo bff para informar o tamanho e ser
 * salvo sem nova requisição; se essa leitura não terminou ou falhou, `baixar` tenta de novo. Só leitura (RN002/RN003).
 */
export function useEmbarcacaoImagemArquivo({
  id,
  tipo,
  url,
  fileName,
}: UseEmbarcacaoImagemArquivoParams) {
  const [arquivo, setArquivo] = useState<ImagemArquivo | null>(null);
  const [falhou, setFalhou] = useState(false);

  // O arquivo vem pelo hub-bff: o site das imagens não envia CORS para o Hub.
  useEffect(() => {
    const controller = new AbortController();

    setArquivo(null);
    downloadEmbarcacaoImagem({ id, tipo, signal: controller.signal })
      .then((file) => setArquivo({ bytes: file.content, mimeType: file.mimeType }))
      .catch(() => undefined);

    return () => controller.abort();
  }, [id, tipo, url]);

  async function baixar() {
    setFalhou(false);

    if (arquivo) {
      downloadFile(arquivo.bytes, fileName, arquivo.mimeType);
      return;
    }

    try {
      const file = await downloadEmbarcacaoImagem({ id, tipo });
      setArquivo({ bytes: file.content, mimeType: file.mimeType });
      downloadFile(file.content, fileName, file.mimeType);
    } catch {
      setFalhou(true);
    }
  }

  return { tamanhoBytes: arquivo ? arquivo.bytes.length : null, falhou, baixar };
}
